/**
 * What caused a change to the ledger.
 *
 * - `broker_order_event` — one event a broker sent about an order. Every event is
 *   recorded, fill or not, since `new` and `canceled` are part of the order's story.
 * - `adjustment` — positions added to or taken out of one account from outside the
 *   ledger: money paid in or out, or holdings the ledger did not see bought.
 * - `transfer` — positions moved between two virtual accounts, with cash going back the
 *   other way.
 * - `split` — a position counted in a different number of units, forward or reverse.
 * - `symbol_change` — a position that now trades under another symbol.
 * - `dividend` — cash a dividend paid into an account.
 */
export type EventRecordKind = 'broker_order_event' | 'adjustment' | 'transfer' | 'split' | 'symbol_change' | 'dividend';

export const EVENT_RECORD_KINDS: ReadonlyArray<EventRecordKind> = ['broker_order_event', 'adjustment', 'transfer', 'split', 'symbol_change', 'dividend'];

export function isEventRecordKind(value: string): value is EventRecordKind {
  return EVENT_RECORD_KINDS.some((kind) => kind === value);
}

/**
 * One thing that happened to the ledger, kept verbatim. Every transaction points at the
 * record that caused it, and the kind lives here rather than on each transaction.
 *
 * `idempotencyKey` is unique, which is what records a redelivered event once: the
 * websocket and the REST backfill both report a fill, and both arrive with the same key.
 */
export interface EventRecord {
  readonly recordId: string;
  readonly kind: EventRecordKind;
  /** Set for a broker's event only. */
  readonly brokerOrderId?: string;
  readonly idempotencyKey: string;
  /** Why, in a person's words, when someone gave one. */
  readonly reason?: string;
  /**
   * What happened, serialised: the broker's event, or the request that made the change.
   * Free-form text, never read by the ledger — only shown, and kept for replay.
   */
  readonly payload: string;
  readonly createdAt: number;
}
